import * as React from 'react';
import { createPortal } from 'react-dom';
import { useFieldContext } from '@/lib/form-context';
import { Calendar } from '@/components/ui/calendar';
import { Field, FieldContent, FieldError, FieldLabel } from '@/components/ui/field';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { type VariantProps } from 'class-variance-authority';
import { schemaFieldVariants } from '../constants';
import { CalendarIcon } from 'lucide-react';

function formatDate(date: Date | string | undefined) {
  if (!date) {
    return '';
  }

  if (typeof date === 'string') {
    date = new Date(date);
  }

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

function setToIso(date: Date | undefined): string {
  if (!date) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isValidDate(date: Date | undefined) {
  if (!date) {
    return false;
  }
  return !isNaN(date.getTime());
}

/**
 * Parses a date string from the API into a local-time Date, avoiding UTC
 * offset issues. Handles both "YYYY-MM-DD" and full ISO timestamp formats.
 */
function parseToLocalDate(value: string | Date | undefined): Date | undefined {
  if (!value) return undefined;
  // Already a Date object — return as-is.
  if (value instanceof Date) return isValidDate(value) ? value : undefined;
  // Try structured YYYY-MM-DD parse first (handles both "YYYY-MM-DD" and full
  // ISO timestamps) to avoid UTC-offset shifting the date.
  const datePart = value.split('T')[0];
  const parts = datePart.split('-').map(Number);
  if (parts.length === 3 && !parts.some(isNaN)) {
    const [year, month, day] = parts;
    const date = new Date(year, month - 1, day); // local midnight — no UTC shift
    if (isValidDate(date)) return date;
  }
  // Fallback: let the browser parse it and accept whatever it gives us.
  const fallback = new Date(value);
  return isValidDate(fallback) ? fallback : undefined;
}

interface DatePickerProps extends VariantProps<typeof schemaFieldVariants> {
  label: string;
  placeholder?: string;
  readOnly?: boolean;
  loading?: boolean;
  disabled?: boolean;
  required?: boolean;
}

/**
 * Returns true once we're certain the primary input mechanism is touch
 * (i.e. a real mobile device). We use a media query rather than UA sniffing
 * so it works correctly on touch-screen laptops too — they still have a mouse
 * as the fine pointer, so (pointer: coarse) stays false for them.
 */
function useIsTouchDevice() {
  const [isTouch, setIsTouch] = React.useState(false);
  React.useEffect(() => {
    setIsTouch(window.matchMedia('(pointer: coarse)').matches);
  }, []);
  return isTouch;
}

export const DatePicker = ({
  label,
  placeholder,
  readOnly,
  loading,
  disabled,
  required,
  variant,
}: DatePickerProps) => {
  const resolvedVariant = variant ?? 'inline';
  const field = useFieldContext<string | Date | undefined>();
  const errors = field.state.meta.errors;
  const hasError = field.state.meta.isTouched && errors.length > 0;
  const isEmpty = !field.state.value;
  const displayErrors = required && isEmpty ? [{ message: `${label} is required` }] : [];

  const isTouchDevice = useIsTouchDevice();
  const date = parseToLocalDate(field.state.value);
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(formatDate(date));
  const isInternalChange = React.useRef(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [calendarPos, setCalendarPos] = React.useState<{ top: number; left: number } | null>(null);

  // Key resets DayPicker's internal navigation only when the field value's
  // month/year changes (e.g. text input, external load). While the user
  // browses inside the dropdown, DayPicker owns its own navigation state —
  // no controlled `month` prop fighting it, so no caption flicker.
  const calendarKey = date ? `${date.getFullYear()}-${date.getMonth()}` : 'empty';

  React.useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    setInputValue(formatDate(date));
  }, [field.state.value]);

  // Compute calendar position from the trigger button whenever the picker opens.
  React.useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setCalendarPos({
      top: rect.bottom + window.scrollY + 4,
      left: rect.right + window.scrollX,
    });
  }, [open]);

  // Close on outside mousedown. We use mousedown (not click) so the calendar
  // closes immediately when the user starts pressing outside — but we check
  // containment on both the trigger and the portal calendar div so that native
  // <select> OS dropdowns inside the calendar never trigger a close (they
  // don't fire mousedown on the document at all while the OS listbox is open).
  const calendarRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    function handleMouseDown(e: MouseEvent) {
      const inContainer = containerRef.current?.contains(e.target as Node);
      const inCalendar = calendarRef.current?.contains(e.target as Node);
      if (!inContainer && !inCalendar) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  return (
    <Field
      orientation={resolvedVariant === 'stacked' ? 'vertical' : 'subgrid'}
      data-invalid={hasError || undefined}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <FieldContent className={resolvedVariant === 'stacked' ? 'w-full' : 'max-w-50'}>
        {
          loading ? (
            <Skeleton className="h-4 w-full" />
          ) : readOnly ? (
            <p>{formatDate(date) || '—'}</p>
          ) : isTouchDevice ? (
            // On touch devices use the native date input so iOS/Android shows
            // their built-in scroll-wheel / calendar sheet picker.
            <input
              id={field.name}
              type="date"
              value={setToIso(date) ?? ''}
              disabled={disabled}
              onBlur={field.handleBlur}
              onChange={(e) => {
                isInternalChange.current = true;
                field.handleChange(e.target.value || undefined);
              }}
              className="h-8 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 dark:bg-input/30"
            />
          ) : (
            // containerRef wraps the input group so mousedown inside it is never
            // treated as an outside-click. The calendar is portalled to document.body
            // to escape card's overflow-hidden.
            <div ref={containerRef} className="relative">
              <InputGroup>
                <InputGroupInput
                  id={field.name}
                  value={inputValue}
                  placeholder={placeholder ?? 'MM/DD/YYYY'}
                  disabled={disabled}
                  onBlur={(e) => {
                    // If the user left a non-empty value that isn't a complete valid
                    // date, push the raw string into the field so Zod's refine
                    // catches it on submit instead of silently ignoring it.
                    const val = e.target.value;
                    if (val && !/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
                      isInternalChange.current = true;
                      field.handleChange(val as any);
                    }
                    field.handleBlur();
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    setInputValue(val);
                    if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
                      const parsed = new Date(val);
                      if (isValidDate(parsed)) {
                        isInternalChange.current = true;
                        field.handleChange(setToIso(parsed));
                      }
                    } else if (val === '') {
                      isInternalChange.current = true;
                      field.handleChange(undefined);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setOpen(true);
                    }
                    if (e.key === 'Escape') {
                      setOpen(false);
                    }
                  }}
                />
                <InputGroupAddon align="inline-end">
                  <InputGroupButton
                    ref={triggerRef}
                    id={`${field.name}-picker`}
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Select date"
                    aria-expanded={open}
                    aria-haspopup="dialog"
                    disabled={disabled}
                    onClick={() => setOpen((v) => !v)}
                  >
                    <CalendarIcon />
                    <span className="sr-only">Select date</span>
                  </InputGroupButton>
                </InputGroupAddon>
              </InputGroup>

              {open &&
                calendarPos &&
                createPortal(
                  <div
                    ref={calendarRef}
                    role="dialog"
                    aria-label="Date picker"
                    style={{
                      position: 'absolute',
                      top: calendarPos.top,
                      left: calendarPos.left,
                      transform: 'translateX(-100%)',
                    }}
                    className={cn(
                      'z-50 overflow-hidden rounded-lg border border-foreground/10',
                      'bg-popover p-0 shadow-md',
                    )}
                  >
                    <Calendar
                      key={calendarKey}
                      mode="single"
                      selected={date}
                      defaultMonth={date}
                      captionLayout="dropdown"
                      onSelect={(selected) => {
                        isInternalChange.current = true;
                        field.handleChange(setToIso(selected)); // Date → "YYYY-MM-DD"
                        setInputValue(formatDate(selected));
                        setOpen(false);
                      }}
                    />
                  </div>,
                  document.body,
                )}
            </div>
          ) /* end desktop custom picker */
        }
        {hasError && <FieldError errors={displayErrors} />}
      </FieldContent>
    </Field>
  );
};
